import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'

import { env } from 'src/config/env'
import { Button } from 'src/shared/components/ui/buttons/button'
import { Form, FormField } from 'src/shared/components/ui/forms/form'
import { InputFormField } from 'src/shared/components/ui/forms/form-fields/input'
import { useConsumerSignInComplete, useConsumerSignInStart } from 'src/shared/features/auth/api'
import AuthCodeInput from 'src/shared/features/auth/components/auth-code-input'
import { useToast } from 'src/shared/hooks/use-toast'
import type { ConsumerLoginEmailFormData, ConsumerLoginOtpFormData } from 'src/shared/schemas/forms/consumer-auth'
import { consumerLoginEmailSchema, consumerLoginOtpSchema } from 'src/shared/schemas/forms/consumer-auth'

type ConsumerLoginFormProps = {
  onSuccess: () => void
  onSwitchView: () => void
}

/** DEPRECATED - use ConsumerEmailLoginView instead */
const ConsumerLoginForm = ({ onSuccess, onSwitchView }: ConsumerLoginFormProps) => {
  const [step, setStep] = useState<'email' | 'otp'>('email')
  const [signInData, setSignInData] = useState<any>(null)
  const { toast } = useToast()
  const [submittedEmail, setSubmittedEmail] = useState('')

  // Email form
  const emailForm = useForm<ConsumerLoginEmailFormData>({
    resolver: zodResolver(consumerLoginEmailSchema),
    mode: 'onChange',
    defaultValues: {
      email: '',
    },
  })

  // OTP form
  const otpForm = useForm<ConsumerLoginOtpFormData>({
    resolver: zodResolver(consumerLoginOtpSchema),
    mode: 'onSubmit',
    defaultValues: {
      otp: '',
    },
  })

  // Hooks for sign-in process
  const signInStart = useConsumerSignInStart()
  const signInComplete = useConsumerSignInComplete()

  const onResendCode = async () => {
    if (signInStart.isPending) {
      return
    }

    try {
      // on log in we dont want to resend sign-up code
      // we resend the sign-in start
      const { email } = emailForm.getValues()
      const result = await signInStart.mutateAsync(email)
      setSignInData(result)

      toast({
        title: 'Code resent',
        description: 'A new verification code has been sent to your email',
      })
    } catch (error) {
      console.error('Resend code error:', error)

      if (error instanceof Error) {
        toast({
          title: `Failed to resend code: ${error.name}`,
          description: error.message || 'Unable to send a new code',
          variant: 'destructive',
        })
      } else {
        toast({
          title: 'Failed to resend code',
          description: 'An unexpected error occurred. Please try again.',
          variant: 'destructive',
        })
      }
    }
  }

  // Handle email submission
  const handleEmailSubmit = async (data: ConsumerLoginEmailFormData) => {
    try {
      const result = await signInStart.mutateAsync(data.email)
      setSignInData(result)
      setStep('otp')
      setSubmittedEmail(data.email)

      toast({
        title: 'Verification code sent',
        description: 'Please check your email for the verification code',
        duration: 5000,
      })
    } catch (error) {
      console.error('Sign-in start error:', error)

      if (error instanceof Error) {
        // For user not found, give a clearer message
        if (error.name === 'UserNotFoundException') {
          toast({
            title: 'Account not found',
            description: 'No account exists with this email. Please sign up first.',
            variant: 'destructive',
          })
          return
        }

        toast({
          title: `Sign in failed: ${error.name}`,
          description: error.message || 'Failed to start sign in process',
          variant: 'destructive',
        })
      } else {
        toast({
          title: 'Sign in failed',
          description: 'An unexpected error occurred. Please try again.',
          variant: 'destructive',
        })
      }
    }
  }

  // Handle OTP submission
  const handleOtpSubmit = async (data: ConsumerLoginOtpFormData) => {
    try {
      await signInComplete.mutateAsync({
        challengeResponse: data.otp,
        signInResult: signInData,
      })

      onSuccess()
    } catch (error) {
      console.error('OTP verification error:', error)

      if (error instanceof Error) {
        toast({
          title: `Verification failed: ${error.name}`,
          description: error.message || 'Failed to verify code',
          variant: 'destructive',
        })
      } else {
        toast({
          title: 'Verification failed',
          description: 'An unexpected error occurred. Please try again with a new code.',
          variant: 'destructive',
        })
      }
    }
  }

  // Go back to email input
  const handleBack = () => {
    setStep('email')
    setSignInData(null)
  }

  return (
    <div className="relative w-full">
      {step === 'email' ? (
        <>
          <h2 className="mb-3 text-center text-[28px] font-bold leading-tight text-primary">Welcome back</h2>
          <Form {...emailForm}>
            <form onSubmit={emailForm.handleSubmit(handleEmailSubmit)} className="flex flex-col gap-3">
              <InputFormField
                control={emailForm.control}
                name="email"
                label="Email address"
                type="email"
                placeholder="Email address"
                fieldClassName="rounded-[10px]"
                required
                errorClassName="hidden"
              />
              <div className="flex flex-col text-center text-[13px]">
                <p className="text-muted">We&apos;ll email you a code to confirm your email. </p>
                <a href={env.LINK_TO_PRIVACY_POLICY} className="font-medium text-primary underline">
                  Privacy Policy
                </a>
              </div>
              <Button type="submit" className="mt-2 w-full rounded-full py-4" isLoading={signInStart.isPending}>
                {signInStart.isPending ? 'Sending Code...' : 'Continue'}
              </Button>
              <div className="flex flex-col gap-1 text-center text-sm font-semibold text-primary">
                <p>
                  Don&apos;t have an account?{' '}
                  <button type="button" onClick={onSwitchView} className="text-primary underline">
                    Sign up
                  </button>
                </p>
              </div>
            </form>
          </Form>
        </>
      ) : (
        <>
          <h2 className="mb-3 text-center text-[28px] font-bold leading-tight text-primary">Enter 8 digit code</h2>
          <Form {...otpForm}>
            <form onSubmit={otpForm.handleSubmit(handleOtpSubmit)} className="flex flex-col gap-3">
              <p className="mb-3 text-center text-base text-muted">
                You&apos;ve received a code at <span className="font-semibold text-primary">{submittedEmail}</span>.
                Please check your inbox and spam folders to retrieve it.{' '}
                <span className="cursor-pointer font-semibold text-primary underline" onClick={onResendCode}>
                  Resend code
                </span>
              </p>

              <FormField
                control={otpForm.control}
                name="otp"
                render={() => (
                  <AuthCodeInput
                    isConsumerLogin
                    inputClassName="mt-1 max-h-[55px] rounded-[10px]"
                    control={otpForm.control}
                    name="otp"
                  />
                )}
              />
              <div className="flex flex-col gap-2">
                <Button type="submit" className="mt-3 w-full rounded-full py-4" isLoading={signInComplete.isPending}>
                  {signInComplete.isPending ? 'Logging in...' : 'Log in'}
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  className="h-auto min-h-0 text-sm font-semibold text-primary underline"
                  onClick={handleBack}
                >
                  Return to log in
                </Button>
              </div>
            </form>
          </Form>
        </>
      )}
    </div>
  )
}

export default ConsumerLoginForm
